'use client';

import { useTranslations } from 'next-intl';
import { useBookingStore } from '@/stores/booking-store';
import { X, Trash2 } from 'lucide-react';
import { IoBedOutline } from 'react-icons/io5';
import { formatShortDate } from '@/lib/calendar-utils';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import { formatPrice } from '@/lib/format';

interface RoomPrice {
  roomSlug: string;
  totalPrice: number;
  currency: string;
  available: boolean;
}

interface SelectionOverviewProps {
  prices: Record<string, RoomPrice>;
  loadingPrices: boolean;
  onClose: () => void;
  onProceed: () => void;
}

export function SelectionOverview({ prices, loadingPrices, onClose, onProceed }: SelectionOverviewProps) {
  const t = useTranslations('selection');
  const { items, checkIn, checkOut, removeRoom } = useBookingStore();

  const hasDates = !!(checkIn && checkOut);

  // Calculate totals (only available rooms contribute to the total)
  const selectedPrices = items
    .map((item) => prices[item.roomSlug])
    .filter((p): p is RoomPrice => !!p && p.available);
  const totalPrice = selectedPrices.reduce((sum, p) => sum + p.totalPrice, 0);
  const currency = selectedPrices[0]?.currency || 'EUR';

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Mobile: bottom sheet / Desktop: centered modal */}
      <div className="fixed inset-0 z-[61] flex items-end md:items-center justify-center pointer-events-none">
        <div
          className="pointer-events-auto w-full md:w-[520px] md:max-h-[80vh] bg-white md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[85vh] animate-slide-up md:animate-none"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <IoBedOutline className="w-5 h-5 text-[#801515]" />
              <h2 className="font-poppins text-lg font-semibold text-[#4e3529]">
                {t('yourSelection')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Dates summary */}
          {hasDates && (
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
              <span className="text-sm text-[#4e3529]/70">
                {formatShortDate(checkIn!)} — {formatShortDate(checkOut!)}
              </span>
              <button
                onClick={() => { onClose(); }}
                className="text-xs text-[#801515]/70 hover:text-[#801515] px-2 py-1 rounded-lg hover:bg-[#801515]/5 transition-all cursor-pointer"
              >
                {t('changeDates')}
              </button>
            </div>
          )}

          {/* Room list */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {items.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                {t('emptySelection')}
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => {
                  const roomPrice = prices[item.roomSlug];
                  const isUnavailable = roomPrice && !roomPrice.available;

                  return (
                    <div
                      key={item.roomSlug}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                        isUnavailable
                          ? 'border-amber-200 bg-amber-50/50'
                          : 'border-gray-100 bg-gray-50/50'
                      }`}
                    >
                      {/* Thumbnail */}
                      <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-200 shrink-0">
                        {item.roomImageUrl ? (
                          <Image
                            src={item.roomImageUrl}
                            alt={item.roomName}
                            width={64}
                            height={48}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <IoBedOutline className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Room info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#4e3529] truncate">
                          {item.roomName}
                        </p>
                        {item.roomTypeName && (
                          <p className="text-xs text-[#4e3529]/50 truncate">
                            {item.roomTypeName}
                          </p>
                        )}
                      </div>

                      {/* Price */}
                      <div className="text-right shrink-0">
                        {loadingPrices ? (
                          <span className="text-xs text-gray-400">...</span>
                        ) : roomPrice ? (
                          <span className={`text-sm font-bold ${isUnavailable ? 'text-amber-600' : 'text-[#801515]'}`}>
                            {isUnavailable ? '—' : formatPrice(roomPrice.totalPrice, roomPrice.currency)}
                          </span>
                        ) : null}
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() => removeRoom(item.roomSlug)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer shrink-0"
                        title={t('removeRoom')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with total + CTA */}
          {items.length > 0 && (
            <div className="px-5 py-4 border-t border-gray-100 bg-white md:rounded-b-2xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#4e3529]/60">
                  {t('roomsCount', { count: items.length })} · {t('total')}
                </span>
                <span className="text-lg font-bold text-[#801515]">
                  {loadingPrices ? '...' : formatPrice(totalPrice, currency)}
                </span>
              </div>
              <button
                onClick={onProceed}
                className="w-full py-3 bg-[#801515] text-white font-bold text-sm uppercase tracking-wider rounded-xl hover:bg-[#6b1111] transition-colors cursor-pointer"
              >
                {t('proceedToCheckout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Slide-up animation for mobile bottom sheet */}
      <style jsx global>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}
